import { useAuth } from "../utils/AuthContext.tsx";
import { useMemo, useState } from "react";
import { TagValueModel, TagRefModel, TagOption } from "../utils/Types.ts";
import TagPopover from "./TagPopover.tsx";
import { Button } from "./ui/button.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQueries } from "@tanstack/react-query";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "./ui/skeleton.tsx";
import { PlusIcon } from "lucide-react";
import { icons, LucideProps } from "lucide-react";
import { IconName } from "../utils/Types.ts";

export interface IconProps extends LucideProps {
  /** Must be one of the keys in the `icons` object */
  name: IconName;
}

export const Icon: React.FC<IconProps> = ({ name, ...props }) => {
  const LucideIcon = icons[name];
  return LucideIcon ? <LucideIcon {...props} /> : null;
};

interface TagCloudProperties {
  selections: TagRefModel[] | undefined;
  options: TagOption[];
  prompt: string;
  onTagAdded?: (value: TagValueModel) => void;
  onTagRemoved?: (value: TagRefModel) => void;
  loading: boolean;
}

class TagSelection {
  public ref: TagRefModel;
  public value: TagValueModel;
  public option: TagOption;

  constructor(ref: TagRefModel, value: TagValueModel, option: TagOption) {
    this.value = value;
    this.ref = ref;
    this.option = option;
  }
}

import { fetchTagValue } from "@/utils/ApiEndpoints.ts";

const TagCloud: React.FC<TagCloudProperties> = (props: TagCloudProperties) => {
  const { token } = useAuth();
  const [open, setOpen] = useState<boolean>(false);
  const selections = props.selections ?? [];

  //dispatches async queries to collect tag values for rendering
  const tagQueries = useQueries({
    queries: selections.map((selection) => ({
      queryKey: ["tags", "values", selection.path],
      queryFn: () => fetchTagValue(selection.path, token!),
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 60, //60 min
      placeholderData: {
        name: "",
        label: "Loading…",
        hint: "?",
        path: selection.path,
        group: "",
        ordinal: 0,
        description: "",
      },
    })),
  });

  const filtered = useMemo(() => {
    //filters out any tags (by path) that are not included as an option for this cloud
    return tagQueries
      .map((q, i) => {
        const value = q.data!;
        if (!value || !value.path) return null;
        const option = props.options.find((o) => value.path.startsWith(`tags/${o.name}`));
        return option ? new TagSelection(selections[i], value, option) : null;
      })
      .filter((x): x is TagSelection => x !== null);
  }, [tagQueries, props.options, selections]);

  const removeTag = (value: TagRefModel) => {
    if (props.onTagRemoved) props.onTagRemoved(value);
  };

  const addTag = (value: TagValueModel) => {
    //check to see if the tag already exists in the list before raising event
    const index = props.selections?.findIndex((m: TagRefModel) => value.path == m.path);
    if (index && props.onTagAdded) props.onTagAdded(value);
    setOpen(false);
  };

  if (props.loading) {
    return (
      <div className="flex gap-2 flex-wrap items-center min-h-8">
        <Skeleton className="w-6 h-4" />
        <Skeleton className="w-6 h-4" />
        <Skeleton className="w-6 h-4" />
      </div>
    );
  }

  return (
    <div className="flex gap-2 flex-wrap items-center min-h-8">
      {filtered &&
        filtered.map((s: TagSelection, i: number) => {
          return (
            <TooltipProvider key={i.toString()}>
              <Tooltip delayDuration={200}>
                <TooltipTrigger>
                  <Button
                    className={`h-6 rounded-sm shadow-none bg-lime-200 text-lime-800 opacity-0 animate-fade-in animation-delay-200}`}
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      removeTag(s.ref);
                    }}
                  >
                    <Icon name={s.option.icon} />
                    {s.value ? s.value.label : "..."}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {!s || s.value.description == "" ? "No description" : s.value.description}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="px-2">
            <PlusIcon strokeWidth={3} />
            {filtered == undefined || filtered.length == 0 ? props.prompt : "Add"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[200px]">
          <DropdownMenuLabel>Add Tag</DropdownMenuLabel>
          <DropdownMenuGroup>
            {props.options &&
              props.options
                //.filter((o) => !o.lock)
                .map((option, index) => (
                  <DropdownMenuSub key={index}>
                    <DropdownMenuSubTrigger
                      onKeyDown={(e: React.KeyboardEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <div className={`w-3 h-3 rounded-[3px] ${option.color}`} />
                      {option.label}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="p-0">
                      <TagPopover option={option} onSelect={addTag} />
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default TagCloud;
